import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Barcode } from "lucide-react";

export default function CoachMeals() {
  const [foods, setFoods] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", brand: "", barcode: "", serving_size: "100g", category: "",
    calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0,
  });

  const load = () => api.get("/foods", { params: q ? { q } : {} }).then((r) => setFoods(r.data));
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [q]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/foods", {
        ...form,
        calories: parseFloat(form.calories) || 0,
        protein: parseFloat(form.protein) || 0,
        carbs: parseFloat(form.carbs) || 0,
        fats: parseFloat(form.fats) || 0,
        fiber: parseFloat(form.fiber) || 0,
      });
      toast.success("Food added");
      setForm({ name: "", brand: "", barcode: "", serving_size: "100g", category: "", calories: 0, protein: 0, carbs: 0, fats: 0, fiber: 0 });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-10" data-testid="coach-meals">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Nutrition</div>
          <h1 className="font-serif text-5xl mt-3">Food database</h1>
          <p className="text-secondary-luxe text-sm mt-3">Foods, recipes, and ingredients available to all clients.</p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(!open)} data-testid="new-food-btn">
          <Plus size={14} /> Add food
        </button>
      </header>

      <div className="flex items-center gap-3 max-w-md">
        <Search size={16} className="text-secondary-luxe" />
        <input className="input-luxe" placeholder="Search foods…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="food-search" />
      </div>

      {open && (
        <form onSubmit={submit} className="card-luxe space-y-5 max-w-4xl" data-testid="food-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><label className="label-luxe">Name</label><input className="input-luxe" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="food-name" /></div>
            <div><label className="label-luxe">Brand</label><input className="input-luxe" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
            <div><label className="label-luxe flex items-center gap-2"><Barcode size={12} /> Barcode</label><input className="input-luxe" value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></div>
            <div><label className="label-luxe">Serving size</label><input className="input-luxe" value={form.serving_size} onChange={(e) => setForm({ ...form, serving_size: e.target.value })} /></div>
            <div><label className="label-luxe">Category</label><input className="input-luxe" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Protein, Healing…" /></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
            {["calories", "protein", "carbs", "fats", "fiber"].map((k) => (
              <div key={k}>
                <label className="label-luxe">{k}</label>
                <input type="number" step="0.1" className="input-luxe" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`food-${k}`} />
              </div>
            ))}
          </div>
          <button className="btn-primary" data-testid="food-submit">Add food</button>
        </form>
      )}

      <div className="border border-luxe">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-xs uppercase tracking-widest text-secondary-luxe">
            <tr>
              <th className="text-left p-4">Food</th>
              <th className="text-left p-4 hidden md:table-cell">Serving</th>
              <th className="text-right p-4">Cal</th>
              <th className="text-right p-4">P</th>
              <th className="text-right p-4">C</th>
              <th className="text-right p-4">F</th>
            </tr>
          </thead>
          <tbody>
            {foods.map((f) => (
              <tr key={f.food_id} className="border-t border-luxe hover:bg-surface-2" data-testid={`food-row-${f.food_id}`}>
                <td className="p-4">
                  <div className="font-medium">{f.name}</div>
                  <div className="text-xs text-tertiary-luxe">{f.brand || f.category}</div>
                </td>
                <td className="p-4 hidden md:table-cell text-secondary-luxe">{f.serving_size}</td>
                <td className="p-4 text-right font-mono-luxe">{Math.round(f.calories)}</td>
                <td className="p-4 text-right font-mono-luxe">{f.protein}</td>
                <td className="p-4 text-right font-mono-luxe">{f.carbs}</td>
                <td className="p-4 text-right font-mono-luxe">{f.fats}</td>
              </tr>
            ))}
            {foods.length === 0 && <tr><td colSpan={6} className="p-6 text-secondary-luxe text-center">No foods.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
